import React from 'react';
import { MapPin, Calendar, Truck, Package, DollarSign, Info, Hash, Clock, Navigation, AlertCircle, Share2 } from 'lucide-react';
import Modal from './Modal';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { selectUserType, selectUser } from '../store/slices/authSlice';
import { generateLoadShareableLink, getLoadDisplayId, getLoadUrlId } from '../utils/loadUtils';
import { toast } from 'react-toastify';
import { formatDate, formatMilitaryTime } from '../utils/dateTimeUtils';

const LoadDetails = ({ load, isOpen, onClose }) => {
  const formatDateTime = (date, time) => {
    if (!date || !time) return 'N/A';
    
    try {
      // Extract just the date part from the ISO string
      const datePart = date.split('T')[0];
      // Create new datetime string
      const dateTimeString = `${datePart}T${time}`;
      const dateObj = new Date(dateTimeString);
      
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date/time:', { date, time });
        return 'Invalid Date';
      }
      
      // Use the utility functions for consistent formatting
      const dateStr = formatDate(dateObj);
      const timeStr = formatMilitaryTime(time);
      
      return `${dateStr} at ${timeStr}`;
    } catch (error) {
      console.error('Date formatting error:', error, { date, time });
      return 'Invalid Date';
    }
  };

  const DetailSection = ({ icon: Icon, title, children, delay }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="bg-white rounded-lg p-4 shadow-sm border border-gray-100"
    >
      <h3 className="text-base font-medium mb-3 flex items-center text-gray-700">
        <Icon className="mr-2 text-blue-500" size={18} />
        {title}
      </h3>
      {children}
    </motion.div>
  );

  const DetailItem = ({ label, value }) => (
    <div className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
      <span className="text-gray-600 font-medium">{label}</span>
      <span className="font-semibold text-gray-800">{value}</span>
    </div>
  );

  // Add user type check using useSelector
  const userType = useSelector(selectUserType);
  const user = useSelector(state => state.auth.user);
  const isCarrier = userType === 'carrier' || userType === 'carrier_admin';

  // New function to determine if address should be visible
  const canViewAddress = React.useMemo(() => {
    if (!isCarrier) return true; // Non-carriers can always see addresses
    
    // Carriers can see address if load is not in 'open' status
    return load.status.toLowerCase() !== 'open';
  }, [isCarrier, load.status]);

  const canShareLoad = React.useMemo(() => {
    if (!userType || !load || !user?.entityId) return false;

    // Extract numeric ID from entityId (e.g., "C123" -> 123 or "R456" -> 456)
    const numericId = parseInt(user.entityId.slice(1));
    
    switch (userType) {
      case 'system_admin':
        return true;
      case 'customer':
      case 'customer_admin':
        return load.customer_id === numericId;
      case 'carrier':
      case 'carrier_admin':
        return load.assigned_to_carrier === numericId;
      case 'driver':
        return load.assigned_driver_id === numericId;
      default:
        return false;
    }
  }, [userType, user, load]);

  const StopCard = ({ stop, index, total }) => (
    <div className="relative pl-4">
      <div className="flex items-start space-x-3 p-4 rounded-lg bg-white border border-gray-100 hover:shadow-sm transition-shadow">
        {/* Timeline indicator */}
        <div className="absolute left-0 top-0 bottom-0 flex flex-col items-center">
          <div className={`w-2.5 h-2.5 rounded-full ${
            stop.stop_type === 'pickup' ? 'bg-blue-500' : 'bg-green-500'
          }`} />
          {index < total - 1 && (
            <div className="w-0.5 flex-grow bg-gray-200 my-1" />
          )}
        </div>

        <div className="flex-grow">
          <div className="flex justify-between items-start gap-4">
            <div>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                stop.stop_type === 'pickup' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'
              }`}>
                {stop.stop_type === 'pickup' ? 'Pickup' : 'Delivery'}
              </span>
              <h4 className="text-sm font-medium mt-1 text-gray-900">
                {stop.city}, {stop.state} {stop.zip_code}
              </h4>
              {canViewAddress ? (
                <p className="text-xs text-gray-500 mt-0.5">{stop.address}</p>
              ) : (
                <p className="text-xs text-amber-500 mt-0.5">
                  <Info className="inline-block w-3 h-3 mr-1" />
                  Address will be visible once load is accepted
                </p>
              )}
            </div>
            
            <div className="text-right text-xs">
              <p className="font-medium text-gray-900">
                {formatDateTime(stop.date, stop.time)}
              </p>
            </div>
          </div>
          
          {stop.notes && canViewAddress && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600"
            >
              {stop.notes}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );

  const handleShare = async () => {
    // Check if user is authenticated first
    if (!userType) {
      toast.warning('Please log in to share load details');
      return;
    }

    const shareableLink = generateLoadShareableLink(getLoadUrlId(load));
    const shareData = {
      title: `Load ${getLoadDisplayId(load)} - ${load.origin_city} to ${load.destination_city}`,
      text: `Freight load from ${load.origin_city}, ${load.origin_state} to ${load.destination_city}, ${load.destination_state}`,
      url: shareableLink
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Load details shared successfully!');
      } else {
        await navigator.clipboard.writeText(shareableLink);
        toast.success('Link copied to clipboard! Note: Recipients will need appropriate permissions to view details.');
      }
    } catch (err) {
      console.error('Error sharing load:', err);
      toast.error('Unable to share load details. Please try again.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col">
        {/* Status warning - removed from scrollable area */}
        {isCarrier && load.status.toLowerCase() === 'open' && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-amber-400 mr-2" />
              <p className="text-sm text-amber-700">
                Detailed address information will be available once you accept this load.
              </p>
            </div>
          </div>
        )}

        {/* Header - removed from scrollable area */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border-b border-gray-200 p-4"
        >
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold flex items-center text-gray-900">
              <Hash className="mr-2 text-blue-500" size={20} />
              Load {getLoadDisplayId(load)}
            </h2>
            <div className="flex items-center space-x-2">
              {canShareLoad && (
                <button
                  onClick={handleShare}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Share load details"
                >
                  <Share2 className="w-5 h-5 text-gray-500" />
                </button>
              )}
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                (load.status || '').toLowerCase() === 'open' ? 'bg-blue-50 text-blue-700' : 
                (load.status || '').toLowerCase().startsWith('in_transit') ? 'bg-yellow-50 text-yellow-700' :
                'bg-green-50 text-green-700'
              }`}>
                {load.status}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Main content - removed max-h and overflow */}
        <div className="p-4 space-y-4">
          {/* Route Information */}
          <DetailSection icon={Navigation} title="Route Information" delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div className="space-y-3">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-700 mb-2">Pickup Location</h4>
                  <p className="text-sm font-medium text-gray-900">{load.origin_city}, {load.origin_state}</p>
                  <p className="text-sm text-gray-600">{formatDateTime(load.pickup_date, load.pickup_time)}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-green-50 p-3 rounded-lg">
                  <h4 className="text-sm font-medium text-green-700 mb-2">Delivery Location</h4>
                  <p className="text-sm font-medium text-gray-900">{load.destination_city}, {load.destination_state}</p>
                  <p className="text-sm text-gray-600">{formatDateTime(load.delivery_date, load.delivery_time)}</p>
                </div>
              </div>
            </div>
          </DetailSection>

          {/* Cargo Details */}
          <DetailSection icon={Package} title="Cargo Details" delay={0.2}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-xs font-medium text-gray-500">Load Type</h4>
                <p className="text-sm font-medium text-gray-900 mt-1">{load.load_type}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-xs font-medium text-gray-500">Weight</h4>
                <p className="text-sm font-medium text-gray-900 mt-1">{load.weight.toLocaleString()} lbs</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-xs font-medium text-gray-500">Pieces</h4>
                <p className="text-sm font-medium text-gray-900 mt-1">{load.pieces.toLocaleString()}</p>
              </div>
              {load.dimensions && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-500">Dimensions</h4>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {`${load.dimensions.length}″ × ${load.dimensions.width}″ × ${load.dimensions.height}″`}
                  </p>
                </div>
              )}
            </div>
          </DetailSection>

          {/* Additional Information */}
          {(load.description || load.special_requirements) && (
            <DetailSection icon={Info} title="Additional Information" delay={0.3}>
              {load.description && (
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <h4 className="text-xs font-medium text-gray-500 mb-1">Description</h4>
                  <p className="text-sm text-gray-900">{load.description}</p>
                </div>
              )}
              {load.special_requirements && (
                <div className="bg-amber-50 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-amber-700 mb-1">Special Requirements</h4>
                  <p className="text-sm text-amber-900">{load.special_requirements}</p>
                </div>
              )}
            </DetailSection>
          )}

          {/* Show route timeline for all users */}
          <DetailSection icon={Navigation} title="Route Timeline" delay={0.4}>
            <div className="space-y-2 mt-2">
              {load.stops?.map((stop, index) => (
                <StopCard 
                  key={stop.id}
                  stop={stop}
                  index={index}
                  total={load.stops.length}
                />
              ))}
            </div>
          </DetailSection>
        </div>
      </div>
    </Modal>
  );
};

export default LoadDetails;
